import { useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';
import { DateTime } from 'luxon';
import ITimeRange from 'types/TimeRange';

export interface IEquipmentInspectionCounts {
  satisfactory: number;
  needsAttention: number;
  unsatisfactory: number;
}

const EQUIPMENT_INSPECTION_COUNTS_QUERY = gql`
  query EquipmentInspectionCounts($startTime: Timestamp!, $endTime: Timestamp!) {
    dashboard {
      equipmentInspectionCounts(startTime: $startTime, endTime: $endTime) {
        satisfactory
        needsAttention
        unsatisfactory
      }
    }
  }
`;

export default function useEquipmentInspectionCounts(timeRange: ITimeRange<DateTime>, enabled: boolean) {
  const graphQLClient = useGraphQLClient();
  const baseKey = useReactQueryBaseKey();

  const startTime = timeRange.startTime.toISO({ suppressMilliseconds: true, includeOffset: false });
  const endTime = timeRange.endTime.toISO({ suppressMilliseconds: true, includeOffset: false });

  const { data, isLoading } = useQuery({
    queryKey: [...baseKey, 'equipment-inspections-dashboard', { startTime, endTime }],
    queryFn: async () => {
      const result = await graphQLClient.request<{
        dashboard: { equipmentInspectionCounts: IEquipmentInspectionCounts };
      }>({
        document: EQUIPMENT_INSPECTION_COUNTS_QUERY,
        variables: { startTime, endTime },
      });
      return result.dashboard.equipmentInspectionCounts;
    },
    enabled,
  });

  return {
    satisfactory: data?.satisfactory ?? 0,
    needsAttention: data?.needsAttention ?? 0,
    unsatisfactory: data?.unsatisfactory ?? 0,
    loading: enabled && isLoading,
  };
}
